import { Tooltip, type TooltipProps } from 'antd';
import React, { type JSX, memo } from 'react';
import { useMediaQuery } from 'react-responsive';

import { InfoIcon } from '@fmbt/ui/icons';
import { LearnMoreLinkInTooltip } from 'components/atoms/LearnMoreLink';
import { MAX_WIDTH_MOBILE } from 'style/constants';

type InlineHelpProps = {
  textContent: string | JSX.Element;
  placement?: TooltipProps['placement'];
  /** Optional id on the tooltip overlay (rc-tooltip). */
  id?: string;
  /** When set, `textContent` is shown with a trailing help-centre “Learn more” link. */
  helpCentreHref?: string;
};

function combineTextContentWithHelpCentreLink(
  textContent: InlineHelpProps['textContent'],
  helpCentreHref: string
): JSX.Element {
  return (
    <>
      {textContent}{' '}
      <LearnMoreLinkInTooltip href={helpCentreHref} />
    </>
  );
}

/**
 * Inline info icon; opens tooltip body from `textContent`. Trigger: click on mobile, hover on desktop.
 * Renders a word space before the icon so call sites stay flush with preceding text.
 */
function InlineHelp(props: InlineHelpProps): JSX.Element {
  const { textContent, helpCentreHref, placement, id } = props;
  const tooltipBody = helpCentreHref
    ? combineTextContentWithHelpCentreLink(textContent, helpCentreHref)
    : textContent;

  const isMobile = useMediaQuery({ maxWidth: MAX_WIDTH_MOBILE });

  return (
    <Tooltip
      id={id}
      title={tooltipBody}
      placement={placement}
      trigger={isMobile ? 'click' : 'hover'}
    >
      <InfoIcon
        size={18}
        style={{ cursor: 'help', marginLeft: '0.25em' }}
        className="InlineHelp-icon" // Keep this className so that SamplePointsTable/styles.less can hide it in print headers (.InlineHelp-icon { display: none; })
      />
    </Tooltip>
  );
}

export default memo(InlineHelp);
